import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Injector, Input, OnChanges, OnInit, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { BaseComponent } from 'app/shared/components';
import { NotificationService, ErrorLoggerService, MetricsService } from 'app/services';
import { FormBuilderDefinition } from 'app/shared/components/form-builder';
import { SectionHandler, SectionHandlerGroup, EditorHandler } from '../entities/entity.handler';
import { InjectEntityHandler } from '../entities';
import { TranslateService } from '@ngx-translate/core';
import { EventFilterService } from 'app/services/events/event-filter.service';
import { AppService } from 'app/app.service';
import { AdminEditorService } from '../admin.service';
import { Subscription, from, of } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
    selector: 'key-admin-entity-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    providers: [EventFilterService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityEditorComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

    @Input() isMobile: boolean;
    @Input() company: string;
    @Input() type: string;
    @Input() id: string;
    @Input() showChanges = true;

    @Output() onEntityLoaded = new EventEmitter<any>();
    @Output() onEntityUpdated = new EventEmitter<any>();
    @Output() onClose = new EventEmitter<void>();

    errorMessage: string;
    @Input() loading = true;
    editing: SectionHandler<any, any> = null;

    form: FormBuilderDefinition;
    values = {};

    handler: EditorHandler;
    handlerType: string;
    entity: any;
    sectionGroups: SectionHandlerGroup<any>[];

    activeEntity: string;
    activeCard: string;

    auditId: string;

    changesVisible = true;
    changesTitle = this.i18n.instant('ADMIN.EDITOR.CHANGES');

    sub: Subscription;

    customPanels: {
        title: string;
        url: SafeResourceUrl;
        activated: boolean;
    }[] = [];

    private metrics = inject(MetricsService);

    constructor(
        private app: AppService,
        private admin: AdminEditorService,
        private injector: Injector,
        private i18n: TranslateService,
        private error: ErrorLoggerService,
        private notify: NotificationService,
        private filters: EventFilterService,
        private ref: ChangeDetectorRef,
        private sanitizer: DomSanitizer
    ) {
        super();
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    ngOnInit() {
        this.on(this.admin.entities.entityUpdated$, entity => {
            if (entity.id === this.id && entity['_reload']) {
                this.load(this.company, this.type, this.id);
            }
        });
    }

    ngOnChanges() {
        if (this.company && this.type && this.id) {
            // for some reason this gets called twice on reload, do some elaborate duplicate checking
            const key = `${this.company}/${this.type}/${this.id}`;
            if (key !== this.activeEntity) {
                this.activeEntity = key;
                this.load(this.company, this.type, this.id);
            }
        }
    }

    load(company: string, type: string, id: string) {

        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = null;
        }

        if (this.handlerType !== type) {
            this.handler = this.getHandler(type);
        }
    
        if (this.handler) {

            const handleError = (err) => {
                this.loading = false;
                if (err.name === 'NotFoundError') {
                    // we could't find the entity, it may have been moved. just close the editor if that happens
                    this.onClose.emit();
                } else {
                    this.showError(err);
                }
                this.ref.markForCheck();
            };

            try {

                this.filters.setOwnerId(company);
                this.editing = null;
                this.handlerType = type;
                this.id = id;
                this.auditId = id.split('.')[0]; // for mutlipart id's, only use the first part for the audit log
                this.loading = true;
                this.errorMessage = null;
                this.changesVisible = this.showChanges && this.app.user.owner.type !== type && this.handler.changesVisible !== false;
                this.ref.markForCheck();

                // getEntity could be an observable or a promise. Convert it to an observable regardless, and handle accordingly
                this.sub = from(id.startsWith('bulk') ? of(null) : this.handler.getEntity(id)).subscribe(async entity => {
                    try {
                        this.entity = entity;
                        this.loadCustomPanels(type, entity);
                        // console.log('entity', this.entity); 
                        this.sectionGroups = await this.handler.getEditorSections(company, this.entity);
                        this.values = this.entity || {};
                        this.loading = false;
                        if ((this.activeCard !== this.changesTitle || !this.changesVisible) && !this.sectionGroups.find(x => x.name === this.activeCard)) {
                            this.activeCard = this.sectionGroups[0] && this.sectionGroups[0].name;
                        }
                        this.onEntityLoaded.emit(this.entity);

                        this.captureAnalytics();

                        this.ref.markForCheck();
                    } catch (err) {
                        handleError(err);
                    }
                }, err => handleError(err));

            } catch (err) {
                handleError(err);
            }

        } else {
            this.loading = false;
            this.showError(new Error(`No handler defined for entity type '${type}'.`));
        }
    }

    loadCustomPanels(type: string, entity: any) {

        this.customPanels = this.app.features.page.admin.customPanels.split('\n').map(str => {

            const [entityType, title, ...rest] = str.split(':');

            if (!entity || entityType !== type) { return null; }

            let url = rest.join(':');

            // substitute some values internal values if they are present in the url
            url = url
                .replace('{token}', this.app.api.accessToken)
                .replace('{language}', this.app.user.language || 'en-us')
                .replace('{user}', this.app.user.id)
                .replace('{client}', this.app.client.id)
                .replace('{id}', entity?.id);

            return {
                title: title,
                url: this.sanitizer.bypassSecurityTrustResourceUrl(url),
                activated: false,
            };
        }).filter(x => x);

    }


    getHandler(type: string) {
        return InjectEntityHandler(this.injector, type);
    }

    showError(error: any) {
        this.error.trackException(error);
        this.errorMessage = error && this.notify.translateError(error);
        this.ref.markForCheck();
    }

    onEntityEditing(section: SectionHandler<any, any>, value: boolean) {
        this.editing = value ? section : null;
    }

    onEntitySaving() {
        this.loading = true;
        this.ref.markForCheck();
    }

    onEntitySaved(entity: any | Error) {
        this.loading = false;
        if (entity instanceof Error) {
            // entity save errors are handled internally by the form editor component
        } else {
            this.values = entity;
            if (this.entity) {
                this.entity = entity;
            }
            this.onEntityUpdated.emit(entity);
        }
        this.ref.markForCheck();
    }


    onCardClick(name: string) {

        // activate custom panels
        this.customPanels.forEach(panel => {
            if (panel.title === name) {
                panel.activated = true;
            }
        });

        this.activeCard = name;
        this.captureAnalytics();
        this.ref.markForCheck();
    }

    private captureAnalytics() { 
        if (['distributor', 'vendor', 'client', 'system'].includes(this.handlerType)) return; 
        
        this.metrics.capture('detail_view', {
            screen: 'administration',
            source: 'main_page',
            tab: this.handlerType,
            value: this.activeCard
        });
    } 
}

